import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import mainBackground from "../../assets/images/background/main_background.png";
import "./BestAppsForPersonalTrainers.css";

// This page provides an in-depth comparison of the most popular
// personal trainer software platforms as of 2025.  Each entry is
// summarised with the key features, pricing structure and a brief
// description of who the tool is best suited for.  The goal is to
// deliver useful, unbiased information to help coaches choose the
// right solution for their business.

const CANONICAL =
  "https://zasfit.io/mejores-apps-entrenadores-personales/";

export default function BestAppsForPersonalTrainers() {
  // Structured data for FAQ schema.  Including common questions
  // improves the chance of rich results in search and helps answer
  // typical queries directly on the results page.  Feel free to
  // extend this with additional questions in the future.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name:
          "¿Qué funcionalidades debería buscar en una app para entrenadores personales?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Busca creación de rutinas con vídeos, planificación de nutrición, seguimiento de progreso, mensajería, pagos integrados y aplicaciones móviles para entrenadores y clientes."
        }
      },
      {
        "@type": "Question",
        name: "¿Qué modelo de precios es más recomendable?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Si gestionas pocos clientes convienen plataformas con coste por cliente. Para gimnasios y estudios grandes, los planes con clientes ilimitados a precio fijo son más económicos a largo plazo."
        }
      }
    ]
  };

  // ItemList schema enumerates the products reviewed.  This helps
  // search engines understand the list order and individual items.
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Zasfit", url: "https://zasfit.io/" },
      { "@type": "ListItem", position: 2, name: "Trainerize", url: "https://www.trainerize.com/" },
      { "@type": "ListItem", position: 3, name: "TrainerFu", url: "https://www.trainerfu.com/" },
      { "@type": "ListItem", position: 4, name: "PT Distinction", url: "https://www.ptdistinction.com/" },
      { "@type": "ListItem", position: 5, name: "WeStrive", url: "https://westrive.com/" },
      { "@type": "ListItem", position: 6, name: "Virtuagym Coach", url: "https://virtuagym.com/" },
      { "@type": "ListItem", position: 7, name: "Everfit", url: "https://everfit.io/" },
      { "@type": "ListItem", position: 8, name: "TrueCoach", url: "https://truecoach.co/" },
      { "@type": "ListItem", position: 9, name: "Hexfit", url: "https://www.myhexfit.com/" },
      { "@type": "ListItem", position: 10, name: "My PT Hub", url: "https://mypthub.net/" }
    ]
  };

  // Article schema gives context about this page for SEO.
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Las mejores apps para entrenadores personales (2025)",
    datePublished: "2025-11-15",
    dateModified: "2025-11-15",
    author: { "@type": "Organization", name: "Zasfit" },
    publisher: { "@type": "Organization", name: "Zasfit" },
    inLanguage: "es-ES",
    mainEntityOfPage: CANONICAL
  };

  return (
    <>
      {/* HERO con el mismo fondo que la landing. Header transparente encima. */}
      <section
        className="hero-wrapper apps-hero text-white"
        style={{
          backgroundImage: `url(${mainBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        <Header />
        <div className="hero-content container">
          <h1 className="hero-title">
            Las mejores Apps<br />  para Entrenadores Personales (2025)
          </h1>
          <p className="hero-subtitle">
            Comparativa real de plataformas: funciones, precios y pros/contras.
          </p>
        </div>
      </section>

      {/* CONTENIDO BLANCO (TU CONTENIDO TAL CUAL) */}
      <main className="container py-4">
        <Helmet>
          <title>Las mejores apps para entrenadores personales (2025)</title>
          <meta
            name="description"
            content="Comparativa completa de plataformas para entrenadores personales en 2025: funcionalidades, precios y pros y contras. Descubre cuál se adapta mejor a tu negocio."
          />
          <link rel="canonical" href={CANONICAL} />
          <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
          <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
          <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        </Helmet>

        {/* Intro sin H1 para evitar título duplicado */}
        <section className="intro mb-4">
          <p>
            Esta guía analiza las opciones líderes en software para entrenadores
            personales en 2025. Se evalúan las funciones principales, el
            modelo de precios y para quién es ideal cada herramienta. Si estás
            empezando o buscas escalar tu negocio de coaching, aquí
            encontrarás la información necesaria para tomar una decisión.
          </p>
        </section>

        <section aria-label="Comparativa">
          <h2>Resumen rápido</h2>
          <p>
            La siguiente tabla sintetiza los puntos clave de cada plataforma:
            sus principales características, el precio de entrada y el tipo
            de entrenador al que mejor se adapta. Más abajo encontrarás un
            análisis detallado de cada herramienta.
          </p>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Funciones principales</th>
                  <th>Precio desde</th>
                  <th>¿Para quién?</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <a href="https://zasfit.io" className="internal-link">
                      Zasfit
                    </a>
                  </td>
                  <td>Rutinas, dietas y seguimiento integrados</td>
                  <td>24,99 €/mes</td>
                  <td>Entrenadores que buscan una solución todo-en-uno</td>
                </tr>

                <tr>
                  <td>Trainerize</td>
                  <td>
                    Biblioteca de ejercicios, creación de programas, integraciones
                    con MyFitnessPal, mensajería y videollamadas
                  </td>
                  <td>≈ 1–2 $/cliente; estudios desde 225 $</td>
                  <td>Coaches individuales y estudios con gran ecosistema</td>
                </tr>
                <tr>
                  <td>TrainerFu</td>
                  <td>
                    Programación y coaching similares a Trainerize, vídeos vía
                    YouTube, integración con MyFitnessPal, flujos automáticos
                  </td>
                  <td>Desde 29 $/mes (20 clientes)</td>
                  <td>Entrenadores pequeños que valoran la automatización</td>
                </tr>
                <tr>
                  <td>PT Distinction</td>
                  <td>
                    Amplia biblioteca de ejercicios, seguimiento de hábitos,
                    actualizaciones de adherencia, 100 dietas predefinidas
                  </td>
                  <td>20 $/mes para 3 clientes</td>
                  <td>Profesionales con enfoque en nutrición y evaluaciones</td>
                </tr>
                <tr>
                  <td>WeStrive</td>
                  <td>
                    Constructor de programas con miles de vídeos, macros,
                    facturación, sitio web personalizado y hábitos
                  </td>
                  <td>Gratis para 1 cliente; ~1–2 $/cliente hasta 125 $/mes</td>
                  <td>Entrenadores que quieren sitio web y app 5 estrellas</td>
                </tr>
                <tr>
                  <td>Virtuagym Coach</td>
                  <td>
                    Paquetes escalables, biblioteca de ejercicios animados,
                    app para clientes y backend para entrenadores, integraciones
                    con wearables
                  </td>
                  <td>29 $/mes (20 clientes)</td>
                  <td>Estudios que necesitan un sistema modular</td>
                </tr>
                <tr>
                  <td>Everfit</td>
                  <td>
                    Planificación automática, biblioteca de vídeos, diarios
                    fotográficos, macros y app para clientes
                  </td>
                  <td>Gratis hasta 5 clientes; 29 $/mes (10 clientes)</td>
                  <td>Pequeños equipos que priorizan la automatización</td>
                </tr>
                <tr>
                  <td>TrueCoach</td>
                  <td>
                    Constructor de entrenamientos basado en texto, biblioteca
                    de 3.000 vídeos, notas de clientes, integración
                    MyFitnessPal
                  </td>
                  <td>Desde 25 $/mes (5 clientes)</td>
                  <td>Entrenadores de fuerza que prefieren simplicidad</td>
                </tr>
                <tr>
                  <td>Hexfit</td>
                  <td>
                    Banco de más de 10.000 ejercicios, creación de
                    entrenamientos, conexión con wearables y calendarios
                  </td>
                  <td>29–139 $/mes según funciones</td>
                  <td>Gimnasios y entrenadores avanzados que personalizan datos</td>
                </tr>
                <tr>
                  <td>My PT Hub</td>
                  <td>
                    Rutinas y nutrición ilimitadas, hábitos, seguimiento,
                    programación de clases, pagos integrados y comunidad
                  </td>
                  <td>Desde 20 $/mes (3 clientes); 52 $/mes ilimitado</td>
                  <td>Estudios con cientos de clientes que buscan tarifa plana</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* --- A partir de aquí TODAS tus secciones tal cual --- */}
       <section className="my-5">
        <h2>Zasfit</h2>
        <p>
          Zasfit es una plataforma todo-en-uno que integra creación de rutinas, planificación de dietas
          y seguimiento del progreso. Permite asignar ejercicios con vídeos propios, controlar calorías
          y macros y ver métricas como peso, grasa corporal y adherencia en tiempo real. Incluye
          aplicaciones nativas para entrenadores y clientes, mensajería integrada y facturación. Los
          planes comienzan en 24,99 € al mes con 7 días de prueba gratuita. Es ideal para entrenadores
          que quieren centralizar su trabajo sin recurrir a múltiples servicios.
        </p>
        <p>
          Si quieres probar una{" "}
          <a href="https://zasfit.io" className="internal-link">
            aplicación para entrenadores personales todo-en-uno
          </a>
          , puedes empezar con Zasfit y descubrir cómo simplificar la gestión de tus clientes.
        </p>
      </section>


        <section className="my-5">
          <h2>Trainerize</h2>
          <p>
            ABC Trainerize es una de las plataformas más veteranas en el
            sector. Ofrece un constructor de programas con una amplia
            biblioteca de ejercicios y la posibilidad de subir vídeos propios.
            El seguimiento nutricional se realiza a través de MyFitnessPal o
            mediante el complemento Evolution Nutrition. Los entrenadores y
            clientes tienen perfiles separados con mensajería integrada y
            notificaciones automáticas para recordar entrenamientos. El coste
            ronda los 1–2 dólares por cliente, y los planes de estudio
            empiezan en unos 225 dólares mensuales para cientos de clientes.
            Es una opción robusta para coaches y estudios que buscan un ecosistema
            completo y personalizable.
          </p>
        </section>

        <section className="my-5">
          <h2>TrainerFu</h2>
          <p>
            TrainerFu ofrece funciones similares a Trainerize con un enfoque
            en la automatización de flujos. Permite subir entrenamientos y
            vídeos a YouTube, integrar MyFitnessPal para seguimiento
            nutricional y organizar programas en grupo con noticias y chat. Los
            planes comienzan en 29 dólares al mes para 20 clientes, con
            escalado a 59 dólares para 60 clientes.
            Destaca por sus workflows automatizados que reducen el tiempo de
            gestión para entrenadores individuales.
          </p>
        </section>

        <section className="my-5">
          <h2>PT Distinction</h2>
          <p>
            PT Distinction combina una potente biblioteca de ejercicios con
            seguimiento de hábitos y un sistema de “salud del programa” que
            alerta sobre la adherencia del cliente. Incluye más de 100 planes
            de comidas predefinidos, diarios fotográficos y evaluaciones
            automatizadas. Se integra con FitBit, MyZone y MyFitnessPal. El
            precio parte de 20 dólares al mes para tres clientes y escala a
            60 dólares para 25 clientes y 80 dólares para 50.
            Es ideal para profesionales que valoran las evaluaciones y la
            personalización nutricional.
          </p>
        </section>

        <section className="my-5">
          <h2>WeStrive</h2>
          <p>
            WeStrive proporciona un constructor de programas con miles de
            vídeos, seguimiento de macros y facturación integrada. Cada
            entrenador recibe un sitio web personalizable con secciones de
            testimonios y venta de programas. Las aplicaciones móviles
            permiten asignar hábitos, pruebas de fitness y encuestas, con
            mensajería individual y grupal. El primer cliente es gratuito y
            luego se paga alrededor de 1–2 dólares por cliente hasta un máximo
            de 125 dólares mensuales para clientes ilimitados.
            Destaca por la experiencia móvil de 5 estrellas y por incluir un
            sitio web en cada cuenta.
          </p>
        </section>

        <section className="my-5">
          <h2>Virtuagym Coach</h2>
          <p>
            Virtuagym Coach utiliza paquetes escalables (Manage, Coach,
            Engage y Grow) para adaptar la solución a cada negocio. Ofrece
            una biblioteca de ejercicios animados, planificaciones
            personalizables y una app para clientes separada del panel de
            administración. Se integra con MyFitnessPal, FitBit,
            AppleHealth y GoogleFit, y permite reservas de clases y
            servicios. El coste del módulo de coaching parte de 29 dólares al
            mes para 20 clientes, con extras para app de marca y funciones
            empresariales. Es recomendable para
            estudios que requieran un sistema modular con muchas
            integraciones.
          </p>
        </section>

        <section className="my-5">
          <h2>Everfit</h2>
          <p>
            Everfit se centra en la automatización y en la experiencia del
            cliente. Dispone de una biblioteca de ejercicios con vídeos,
            permite añadir rutinas propias mediante YouTube o Vimeo, ofrece
            diarios de comidas con fotos y establece objetivos de macronutrientes.
            La mensajería se realiza dentro de la app, aunque no hay
            procesamiento de pagos integrado. El plan Starter es gratuito
            hasta 5 clientes; el plan Pro cuesta 29 dólares al mes para 10
            clientes e incluye automatizaciones y branding personalizado; el
            plan Studio para hasta 100 clientes cuesta 165 dólares al mes.
            Adecuado para entrenadores que priorizan la experiencia del
            cliente y la eficiencia del flujo de trabajo.
          </p>
        </section>

        <section className="my-5">
          <h2>TrueCoach</h2>
          <p>
            TrueCoach fue creado por coaches y se caracteriza por su
            sencillez. Funciona como un cuaderno digital: los entrenadores
            escriben las sesiones en texto, los clientes registran sus
            resultados y ambas partes se comunican mediante comentarios.
            Cuenta con más de 3.000 vídeos de ejercicios, integraciones con
            MyFitnessPal y Stripe para pagos y soporte para PDFs y
            fotos. Los planes empiezan en 25 dólares
            al mes para 5 clientes y llegan a 119 dólares para 50 clientes.
            Es ideal para entrenadores de fuerza que buscan rapidez y
            simplicidad sin elementos visuales complejos.
          </p>
        </section>

        <section className="my-5">
          <h2>Hexfit</h2>
          <p>
            Hexfit es una herramienta avanzada para gimnasios y entrenadores
            profesionales. Ofrece un banco de más de 10.000 ejercicios en
            imágenes o animaciones y permite crear nuevos ejercicios con
            vídeos propios. Se integra con pulseras Polar, Fitbit y Strava
            para sincronizar datos, y centraliza evaluaciones, cuestionarios,
            calendarios y videoconferencia. El precio
            se adapta al tamaño del equipo y varía entre 29 y 139 dólares al mes.
            Recomendado para quienes necesitan un seguimiento exhaustivo y
            personalizable.
          </p>
        </section>

        <section className="my-5">
          <h2>My PT Hub</h2>
          <p>
            My PT Hub se distingue por permitir clientes ilimitados a un
            precio fijo. Incluye programación de entrenamientos con más de
            8.000 ejercicios, planificación de nutrición, coaching de hábitos,
            seguimiento de métricas, reservas de sesiones y pagos
            integrados. La plataforma tiene una comunidad interna y un
            completo panel de análisis. El plan Starter cuesta unos 20 dólares
            mensuales para tres clientes, mientras que el plan Premium
            (unos 52 dólares al mes al facturar anualmente) ofrece clientes
            ilimitados y todas las funciones. Es una
            buena opción para estudios y entrenadores con grandes carteras
            que necesitan escalabilidad sin costes crecientes.
          </p>
        </section>
      </main>

      <Footer />
    </>
  );
}
